import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildPath,
  computeDomain,
  makeGeometry,
  xScale,
  yScale,
  yTicks,
  xDateTicks,
  type Series,
} from "../lib/chart";

interface Props {
  series: readonly Series[];
  /** Optional ISO timestamp at which to draw a vertical "now" marker. */
  nowMarker?: string | null;
  /** Y-axis unit label, e.g. "€/MWh". */
  unit?: string;
  /** Pixel height of the chart. Width is responsive. */
  height?: number;
  /** Number formatter for y-axis and tooltip values. */
  formatValue?: (v: number) => string;
  /** Render with a darker grid (used on cards with darker backgrounds). */
}

const PRICE_FMT = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function LineChart({
  series,
  nowMarker,
  unit,
  height = 280,
  formatValue,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [hoverX, setHoverX] = useState<number | null>(null);

  // Resize observer so the chart stays responsive without an external lib.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(280, Math.floor(entry.contentRect.width)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const valueFmt = formatValue ?? ((v: number) => PRICE_FMT.format(v));

  const visibleSeries = useMemo(() => series.filter((s) => s.points.some((p) => p.y !== null)), [series]);
  const domain = useMemo(() => computeDomain(visibleSeries), [visibleSeries]);
  // Detect whether the x-axis is datetime (e.g. 15-min spot) or date-only
  // (e.g. daily futures settlements). Drives the tooltip header format —
  // showing "13:45" makes no sense on a 20-day futures curve, and showing
  // just "29.05.26" on the 15-min chart loses precision.
  const hasTimeAxis = useMemo(
    () =>
      visibleSeries.some((s) =>
        s.points.some((p) => typeof p.x === "string" && /T\d{2}:\d{2}/.test(p.x)),
      ),
    [visibleSeries],
  );
  // Sampling granularity of the densest non-step series — used to snap the
  // tooltip's reported timestamp to a real bin (e.g. always show 00:00 /
  // 00:15 / 00:30 on the 15-min spot, never an intermediate 00:07). Step
  // overlays like Base/Peak are skipped because their points are placed at
  // fence-post positions (start + end of each window) and would distort the
  // detection.
  const snapMs = useMemo(() => detectSampleInterval(visibleSeries), [visibleSeries]);

  if (!domain) {
    return (
      <div
        ref={containerRef}
        className="flex items-center justify-center text-xs text-slate-500 dark:text-slate-400"
        style={{ height }}
      >
        Keine Daten in der aktuellen Auswahl.
      </div>
    );
  }

  const geo = makeGeometry(width, height, domain);
  const yt = yTicks(domain, valueFmt);
  const xt = xDateTicks(domain);

  const nowMs = nowMarker ? Date.parse(nowMarker) : NaN;
  const showNow = Number.isFinite(nowMs) && nowMs >= domain.xMin && nowMs <= domain.xMax;

  // Crosshair / tooltip: track mouse / touch position; project to time domain.
  const handlePointer = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    if (localX < geo.paddingLeft || localX > geo.paddingLeft + geo.innerWidth) {
      setHoverX(null);
      return;
    }
    const fraction = (localX - geo.paddingLeft) / geo.innerWidth;
    setHoverX(domain.xMin + fraction * (domain.xMax - domain.xMin));
  };

  // For the tooltip we pick the nearest sample per series. Each row carries
  // the optional per-point `meta` (e.g. historical contract name) so the
  // tooltip can show it without an extra lookup.
  const tooltipRows = useMemo(() => {
    if (hoverX === null) return [];
    type Row = { series: Series; dt: number; y: number; x: string; meta?: string };
    return visibleSeries
      .map((s): Row | null => {
        let best: { dt: number; y: number; x: string; meta?: string } | null = null;
        for (const p of s.points) {
          if (p.x === null || p.y === null) continue;
          const t = Date.parse(p.x);
          if (!Number.isFinite(t)) continue;
          const dt = Math.abs(t - hoverX);
          if (!best || dt < best.dt) best = { dt, y: p.y, x: p.x, meta: p.meta };
        }
        return best ? { series: s, ...best } : null;
      })
      .filter((r): r is Row => r !== null)
      .sort((a, b) => b.y - a.y);
  }, [hoverX, visibleSeries]);

  const tooltipX = hoverX !== null ? xScale(geo, hoverX) : null;

  return (
    <div ref={containerRef} className="relative">
      <svg
        width={width}
        height={height}
        role="img"
        onPointerMove={handlePointer}
        onPointerLeave={() => setHoverX(null)}
        className="touch-none"
      >
        {/* Grid + y-axis */}
        {yt.map((t, i) => {
          const y = yScale(geo, t.position);
          return (
            <g key={`y-${i}`}>
              <line
                x1={geo.paddingLeft}
                x2={geo.paddingLeft + geo.innerWidth}
                y1={y}
                y2={y}
                stroke="currentColor"
                className="text-slate-200/60 dark:text-slate-700/40"
                strokeWidth={1}
              />
              <text
                x={geo.paddingLeft - 6}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-slate-500 dark:fill-slate-400"
                style={{ fontSize: 10 }}
              >
                {t.label}
              </text>
            </g>
          );
        })}
        {/* Zero line for negative-price legibility */}
        {domain.yMin < 0 && domain.yMax > 0 && (
          <line
            x1={geo.paddingLeft}
            x2={geo.paddingLeft + geo.innerWidth}
            y1={yScale(geo, 0)}
            y2={yScale(geo, 0)}
            stroke="currentColor"
            className="text-slate-400 dark:text-slate-500"
            strokeWidth={1}
          />
        )}
        {/* x-axis ticks */}
        {xt.map((t, i) => {
          const x = xScale(geo, t.position);
          return (
            <g key={`x-${i}`}>
              <line
                x1={x}
                x2={x}
                y1={geo.paddingTop + geo.innerHeight}
                y2={geo.paddingTop + geo.innerHeight + 4}
                stroke="currentColor"
                className="text-slate-400 dark:text-slate-500"
                strokeWidth={1}
              />
              <text
                x={x}
                y={geo.paddingTop + geo.innerHeight + 16}
                textAnchor="middle"
                className="fill-slate-500 dark:fill-slate-400"
                style={{ fontSize: 10 }}
              >
                {t.label}
              </text>
            </g>
          );
        })}
        {/* Now marker */}
        {showNow && (
          <line
            x1={xScale(geo, nowMs)}
            x2={xScale(geo, nowMs)}
            y1={geo.paddingTop}
            y2={geo.paddingTop + geo.innerHeight}
            stroke="currentColor"
            strokeDasharray="2 3"
            className="text-slate-400 dark:text-slate-500"
            strokeWidth={1}
          />
        )}
        {/* Series paths */}
        {visibleSeries.map((s) => (
          <path
            key={s.id}
            d={buildPath(geo, s)}
            fill="none"
            stroke={s.color}
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={s.dashed ? "5 4" : undefined}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {/* Hover crosshair */}
        {tooltipX !== null && (
          <line
            x1={tooltipX}
            x2={tooltipX}
            y1={geo.paddingTop}
            y2={geo.paddingTop + geo.innerHeight}
            stroke="currentColor"
            className="text-slate-300 dark:text-slate-600"
            strokeWidth={1}
          />
        )}
      </svg>
      {/* Tooltip */}
      {tooltipX !== null && tooltipRows.length > 0 && (
        <div
          className="pointer-events-none absolute z-10 bg-slate-900/95 dark:bg-slate-800/95 text-white text-xs rounded-md shadow-lg px-2.5 py-1.5 ring-1 ring-slate-700"
          style={{
            left:
              tooltipX > width - 160
                ? Math.max(8, tooltipX - 170)
                : Math.min(width - 160, tooltipX + 12),
            top: 10,
            minWidth: 150,
          }}
        >
          <div className="text-[10px] uppercase tracking-wide text-slate-300 mb-1">
            {fmtTooltipDate(snapToInterval(hoverX ?? NaN, snapMs), hasTimeAxis)}
            {unit ? <span className="ml-1 text-slate-400">{unit}</span> : null}
          </div>
          <ul className="space-y-0.5">
            {tooltipRows.map((r) => (
              <li key={r.series.id} className="flex items-center gap-2 leading-tight">
                <span
                  className="inline-block w-2 h-2 rounded-sm"
                  style={{ backgroundColor: r.series.color }}
                />
                <span className="grow truncate">
                  {r.series.label}
                  {r.meta && (
                    <span className="ml-1 text-slate-400 font-normal">· {r.meta}</span>
                  )}
                </span>
                <span className="tabular-nums">{valueFmt(r.y)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Find the smallest positive consecutive x-distance across every non-step
 * series. That's a robust estimate of the chart's sampling cadence:
 *   - 15-min spot → 900_000 ms
 *   - daily futures / EGSI / forwards → 86_400_000 ms
 * Returns 60_000 ms (1 min) when nothing usable is found, so snapping
 * degrades to "nearest minute" instead of throwing.
 */
function detectSampleInterval(series: readonly Series[]): number {
  let minDiff = Infinity;
  for (const s of series) {
    if (s.step) continue;
    let prev: number | null = null;
    for (const p of s.points) {
      if (p.x === null || p.y === null) continue;
      const t = Date.parse(p.x);
      if (!Number.isFinite(t)) continue;
      if (prev !== null) {
        const diff = t - prev;
        if (diff > 0 && diff < minDiff) minDiff = diff;
      }
      prev = t;
    }
  }
  return Number.isFinite(minDiff) ? minDiff : 60_000;
}

function snapToInterval(ms: number, intervalMs: number): number {
  if (!Number.isFinite(ms) || !Number.isFinite(intervalMs) || intervalMs <= 0) return ms;
  // For day-level and longer intervals, don't snap: rounding to a UTC-day
  // boundary can flip the rendered date by ±1 day in Europe/Berlin (which
  // is up to 2 h offset). The locale formatter handles date-only display
  // correctly without help.
  if (intervalMs >= 24 * 3600 * 1000) return ms;
  return Math.round(ms / intervalMs) * intervalMs;
}

function fmtTooltipDate(msEpoch: number, withTime: boolean): string {
  if (!Number.isFinite(msEpoch)) return "";
  const d = new Date(msEpoch);
  if (withTime) {
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Berlin",
    });
  }
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    timeZone: "Europe/Berlin",
  });
}
